/**
 * API 错误 → 表单展示的映射(契约 guidelines §2:分支只用 error.code;
 * error.message 是服务端拥有的中文用户文案,直接展示,绝不客户端重组)。
 */
import { ApiError, ApiResponseError, ApiTimeoutError, ApiTransportError } from '@web-v4/api-client';

/** 字段名 → 首条错误消息,key 与后端 error.fields 对齐(captcha 已归一)。 */
export type FieldMessages = Record<string, string>;

export interface FormErrorState {
  /** 表单级错误(role="alert" 展示在表单顶部)。 */
  form?: string;
  /** 字段级错误。 */
  fields: FieldMessages;
}

/** 后端 captcha 相关错误可能落在 captcha / captcha_answer / captcha_id 上,统一归到 captcha 控件。 */
const CAPTCHA_FIELD_ALIASES: readonly string[] = ['captcha', 'captcha_answer', 'captcha_id'];

/**
 * 把一次提交抛出的错误规整为表单展示状态:
 * - ApiError(错误 envelope):fields 逐字段取首条消息;无 fields 时 message
 *   作为表单级错误(message 是服务端用户文案,可直接展示);
 * - 传输/超时/契约破坏:给通用表单级文案(这类是代理/后端故障,不是用户错误)。
 */
export function toFormError(error: unknown): FormErrorState {
  if (error instanceof ApiError) {
    const fields: FieldMessages = {};
    if (error.fields) {
      for (const [name, messages] of Object.entries(error.fields)) {
        const first = messages[0];
        if (first === undefined) {
          continue;
        }
        if (CAPTCHA_FIELD_ALIASES.includes(name)) {
          fields.captcha ??= first;
        } else {
          fields[name] ??= first;
        }
      }
    }
    // 有字段错误时表单顶部不再重复;无字段错误时展示服务端文案。
    return Object.keys(fields).length > 0 ? { fields } : { form: error.message, fields };
  }
  if (error instanceof ApiTimeoutError) {
    return { form: '请求超时,请检查网络后重试。', fields: {} };
  }
  if (error instanceof ApiTransportError) {
    return { form: '网络连接失败,请检查网络后重试。', fields: {} };
  }
  if (error instanceof ApiResponseError) {
    return { form: '服务响应异常,请稍后重试。', fields: {} };
  }
  return { form: '发生未知错误,请稍后重试。', fields: {} };
}
